import { useEffect, useState } from 'react';
import { CreditCard, Calendar, CheckCircle2, AlertCircle } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { supabase } from '@/integrations/supabase/client';
import {
  computeBillingStatus, billingStatusClasses, billingStatusLabel,
  formatMoney,
} from '@/lib/billingStatus';
import { getPrimaryCoachLink } from '@/lib/primaryCoach';

interface Props {
  userId: string | undefined;
}

interface BillingData {
  monthly_amount: number | null;
  billing_day: number | null;
  last_payment_date: string | null;
  payment_status: string | null;
}

interface Payment {
  id: number;
  amount: number;
  payment_date: string;
  method: string;
  period_label: string | null;
}

/**
 * Client-side "Mi cuenta" section. Read-only view of the client's
 * billing config and recent payment history. The coach is the only
 * one who can register payments — the client just sees what's there.
 *
 * Hidden when the coach hasn't configured monthly_amount yet (no point
 * in showing 'Sin información' to the client; better to hide the
 * section entirely until the coach fills it out).
 */
export function BillingSection({ userId }: Props) {
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [link, { data: hist }] = await Promise.all([
        // Billing config del coach PRINCIPAL (no del más reciente).
        // Antes agarrábamos coach_clients.order(created_at desc).limit(1)
        // que devolvía el último asignado — podía ser un coach-test
        // y mostrar monto incorrecto.
        getPrimaryCoachLink<{
          monthly_amount?: number | null;
          billing_day?: number | null;
          last_payment_date?: string | null;
          payment_status?: string | null;
        }>(userId, 'monthly_amount, billing_day, last_payment_date, payment_status'),
        supabase
          .from('client_payments')
          .select('id, amount, payment_date, method, period_label')
          .eq('client_id', userId)
          .order('payment_date', { ascending: false })
          .limit(6),
      ]);
      if (cancelled) return;
      setBilling(link as any);
      setPayments((hist || []) as any);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Realtime — payments registered by the coach show up immediately.
  useEffect(() => {
    if (!userId) return;
    const ch = supabase
      .channel(`client-billing-${userId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'client_payments', filter: `client_id=eq.${userId}` },
        async () => {
          const { data: hist } = await supabase
            .from('client_payments')
            .select('id, amount, payment_date, method, period_label')
            .eq('client_id', userId)
            .order('payment_date', { ascending: false })
            .limit(6);
          setPayments((hist || []) as any);
          // Re-pull billing del coach principal (mismo helper que la
          // carga inicial — fuente única de verdad para "coach del
          // cliente").
          const link = await getPrimaryCoachLink<BillingData>(
            userId,
            'monthly_amount, billing_day, last_payment_date, payment_status'
          );
          if (link) setBilling(link as any);
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId]);

  if (loading || !billing || !billing.billing_day || !billing.monthly_amount) {
    return null;
  }

  const status = computeBillingStatus(billing.billing_day, billing.last_payment_date);
  const nextDueLabel = status.nextDueDate
    ? status.nextDueDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <section id="cuenta" className="scroll-mt-32">
      <SectionHeader title="Mi cuenta" subtitle="Pagos" icon={CreditCard} helpId="mi-cuenta" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {/* Próximo cobro */}
        <div className={`rounded-xl border-2 p-4 ${billingStatusClasses(status.status)}`}>
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4" />
            <span className="text-[10px] font-black uppercase tracking-widest">
              {status.paidThisCycle ? 'Próximo cobro' : 'Cobro pendiente'}
            </span>
          </div>
          <div className="text-2xl md:text-3xl font-black">
            {formatMoney(billing.monthly_amount)}
          </div>
          <div className="text-xs mt-1 opacity-80">
            {status.status === 'overdue'
              ? `Atrasado · venció el ${nextDueLabel}`
              : status.status === 'due_today'
                ? 'Vence hoy'
                : `Vence ${nextDueLabel}`}
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider">
            {status.status === 'paid' || status.paidThisCycle ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            {billingStatusLabel(status.status)}
          </div>
        </div>

        {/* Último pago */}
        <div className="rounded-xl border-2 border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Último pago registrado
            </span>
          </div>
          {billing.last_payment_date ? (
            <>
              <div className="text-xl md:text-2xl font-black">
                {new Date(billing.last_payment_date + 'T12:00:00').toLocaleDateString('es-MX', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </div>
              {payments[0] && (
                <div className="text-xs text-muted-foreground mt-1">
                  {formatMoney(payments[0].amount)} · {payments[0].method}
                  {payments[0].period_label && ` · ${payments[0].period_label}`}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-muted-foreground italic">Aún no hay pagos registrados</div>
          )}
        </div>
      </div>

      {payments.length > 0 && (
        <div className="mt-4 md:mt-6 rounded-xl border-2 border-border bg-card overflow-hidden">
          <div className="px-4 py-2.5 bg-secondary/50 border-b border-border">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Historial reciente
            </span>
          </div>
          <div className="divide-y">
            {payments.map(p => (
              <div key={p.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold">
                    {new Date(p.payment_date + 'T12:00:00').toLocaleDateString('es-MX', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {p.method}{p.period_label && ` · ${p.period_label}`}
                  </div>
                </div>
                <div className="text-sm font-black text-accent shrink-0">
                  {formatMoney(p.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
